import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import ProposalDeck from "@/components/ProposalDeck";
import { ProposalProvider } from "@/lib/proposalContext";
import { Printer } from "lucide-react";

/**
 * Anonymous proposal share link.
 *
 * Renders the exact same multi-page deck a salesperson sees — but the
 * quote payload is fetched from the unauthenticated
 * `/api/public/quotes/{token}` endpoint so anyone with the link can
 * view. No sidebar, no other-route access; the URL is scoped to this
 * proposal only.
 *
 * The share link is generated by the salesperson via `POST
 * /api/quotes/{id}/share-token` (see the "Copy share link" button on
 * the Quote editor). Revoking rotates the token so any previously-
 * distributed URL 404s immediately.
 */
export default function PublicProposal() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [settings, setSettings] = useState(null);
  const [state, setState] = useState({ loading: true, error: null });

  useEffect(() => {
    let cancelled = false;
    // Plain axios (no auth interceptor) — the public endpoint must be
    // callable without a JWT. Using our authed `api` instance would
    // still work, but it also fires a refresh-on-401 which is noisy
    // for a logged-out visitor.
    const base = process.env.REACT_APP_BACKEND_URL;
    axios
      .get(`${base}/api/public/quotes/${token}`)
      .then((res) => {
        if (cancelled) return;
        setQuote(res.data.quote);
        setSettings(res.data.settings);
        setState({ loading: false, error: null });
      })
      .catch((e) => {
        if (cancelled) return;
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        setState({
          loading: false,
          error:
            status === 410
              ? detail || "This proposal is no longer available."
              : status === 404
                ? "This share link is no longer active."
                : "Unable to load proposal.",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (state.loading) {
    return <div className="min-h-screen flex items-center justify-center bg-white text-gray-600">Loading proposal…</div>;
  }
  if (state.error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white text-gray-700 p-8 text-center">
        <div className="text-2xl font-bold mb-2 text-[#0f2b3d]">Proposal unavailable</div>
        <div className="text-sm text-gray-500 max-w-md">{state.error}</div>
      </div>
    );
  }

  return (
    <ProposalProvider mode="public" token={token}>
      <div className="no-print-toolbar bg-[#E6F2FA] py-3 flex justify-center items-center gap-3 sticky top-0 z-50 text-[#011836]">
        <div className="text-xs uppercase tracking-[0.25em] text-[#011836]/70 hidden sm:block">
          {settings?.company_name || "Southern Ocean Solutions"} · Proposal {quote?.number}
        </div>
        <button
          onClick={() => window.print()}
          className="px-4 py-2 rounded bg-[#0088CC] hover:bg-[#006DA6] text-white text-sm font-semibold inline-flex items-center gap-2 transition-colors"
          data-testid="public-print-btn"
        >
          <Printer className="h-4 w-4" />
          Print / Save as PDF
        </button>
      </div>
      <ProposalDeck quote={quote} settings={settings} />
    </ProposalProvider>
  );
}
